import React, { useContext } from 'react';
import {
  Animated, StyleSheet, View,
} from 'react-native';
import { reverse } from 'lodash/fp';
import Color from 'color';

import MultiSelectContext from '../../contexts/MultiSelect';
import { DraggableFlatListContext } from '../DraggableFlatList/contexts/DraggableFlatListContext';
import { DragAndDropContext, STATUS } from '../DraggableFlatList/contexts/DragAndDropContext';
import useDimensions from '../../hooks/dimensions';
import ActionsContext from './ActionsContext';
import ItemDetail from './ItemDetail';


const ACTION_PADDING = 10,
      ACTION_ICON_SIZE = 20,
      ACTION_ICON_HALF = (ACTION_PADDING + ACTION_ICON_SIZE) / 2,
      FADE_DISTANCE = 10,
      ACTION_EDGE_WIDTH = 5,
      HALF_FADE_DISTANCE = FADE_DISTANCE / 2;


const styles = StyleSheet.create({
  box: {
    flex: 1,
    zIndex: 99,
    height: '100%',
  },
  colorBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    justifyContent: 'center',
    bottom: 0,
    right: 0,
    shadowOpacity: 0.4,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowColor: 'black',
    shadowRadius: 1,
  },
  container: { flex: 1 },
  wrapper: {
    flex: 1, alignItems: 'center', flexDirection: 'row', height: '100%',
  },
  innerWrapper: {
    flex: 1, alignItems: 'center', flexDirection: 'row',
  },
  actionText: {
    color: 'white', textTransform: 'uppercase', fontSize: 14, padding: ACTION_PADDING, paddingHorizontal: 0,
  },
  icon: {
    padding: ACTION_PADDING,
  },
  innerShadow: { backgroundColor: 'rgba(0,0,0,0.1)', width: '100%', height: '100%' },
  wrapperRight: { justifyContent: 'flex-end' },
  upperShadow: {
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 1,
    shadowRadius: 1,
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: 1,
    backgroundColor: 'rgba(0,0,0,0.1)',
    opacity: 1,
  },
});

const interpolations = {
  fadeOut: {
    inputRange: [0, 1],
    outputRange: [1, 0],
    extrapolate: 'clamp',
  },
};

const Icon = ({ IconSet, iconName }) => IconSet
  ? (
    <IconSet
      color='white'
      name={iconName}
      size={ACTION_ICON_SIZE}
      style={styles.icon}
    />
  )
  : null;

const AnimateIconToCenter = ({
  isBeingCommited, fromLeft, commitAnimationProgress, action,
}) => {
  const { width } = useDimensions('window'),
        { title, iconName, IconSet } = action;

  const children = [
    <Icon
      key='icon'
      iconName={iconName}
      IconSet={IconSet}
    />,
    <Text
      key='text'
      commitAnimationProgress={commitAnimationProgress}
      isBeingCommited={isBeingCommited}
      title={title}
    />];

  return isBeingCommited
    ? (
      <Animated.View style={[styles.innerWrapper, !fromLeft && styles.wrapperRight, {
        transform: [{
          translateX: commitAnimationProgress.interpolate({
            inputRange: [0, 1],
            outputRange: [0, fromLeft
              ? (width / 2) - ACTION_ICON_HALF
              : -(width / 2) + ACTION_ICON_HALF],
            extrapolate: 'clamp',
          }),
        }],
      }]}
      >
        {fromLeft ? children : reverse(children)}
      </Animated.View>
    )
    : <>{fromLeft ? children : reverse(children)}</>;
};


const Text = ({ isBeingCommited, commitAnimationProgress, title }) => {
  const opacity = isBeingCommited
    ? commitAnimationProgress.interpolate(interpolations.fadeOut)
    : 1;
  return (
    <Animated.Text style={[styles.actionText, {
      opacity,
    }]}
    >
      { title }
    </Animated.Text>
  );
};

const ActionsPart = ({ actualTranslateX }) => {
  const {
    leftActions, rightActions, x, commitedAction, commitAnimationProgress,
  } = useContext(ActionsContext);

  const translateX = commitedAction ? x : actualTranslateX,
        { width } = useDimensions('window');

  return (
    <>
      { reverse(leftActions).map((action, index, arr) => {
        const { triggerAt: nextTriggerAt } = arr[index - 1] || { triggerAt: 100000000 },
              {
                triggerAt, title, color,
              } = action,
              isBeingCommited = commitedAction === action;

        return (
          <Animated.View
            key={title}
            style={[
              styles.colorBackground,
              {
                opacity: translateX.interpolate({
                  inputRange: [-1, 0, 1],
                  outputRange: [0, 1, 1],
                }),
                transform: index === 0 ? [] : [{
                  translateX: isBeingCommited ? commitAnimationProgress.interpolate({
                    inputRange: [0, 1],
                    outputRange: [index * ACTION_EDGE_WIDTH, 0],
                    extrapolate: 'clamp',
                  }) : translateX.interpolate({
                    inputRange: [
                      nextTriggerAt - HALF_FADE_DISTANCE,
                      nextTriggerAt + HALF_FADE_DISTANCE,
                      width,
                    ],
                    outputRange: [
                      ACTION_EDGE_WIDTH * index,
                      nextTriggerAt - ((arr.length - index) * ACTION_EDGE_WIDTH),
                      width - ((arr.length - index) * ACTION_EDGE_WIDTH),
                    ],
                    extrapolate: 'clamp',
                  }),
                }],
              },
            ]}
          >
            <View style={[styles.wrapper, {
              backgroundColor: color,
            }]}
            >
              <Animated.View
                elevation={2}
                style={[styles.wrapper, {
                  backgroundColor: color,
                  opacity: commitedAction ? isBeingCommited ? 1 : 0.5 : translateX.interpolate({
                    inputRange: [
                      triggerAt - HALF_FADE_DISTANCE,
                      triggerAt,
                      nextTriggerAt - HALF_FADE_DISTANCE,
                      nextTriggerAt,
                    ],
                    outputRange: [0.5, 1, 1, 0.5],
                    extrapolate: 'clamp',
                  }),
                }]}
              >
                <AnimateIconToCenter
                  fromLeft
                  commitAnimationProgress={commitAnimationProgress}
                  isBeingCommited={isBeingCommited}
                  action={action}
                />
              </Animated.View>
            </View>
          </Animated.View>
        );
      }) }

      { rightActions.map((action, index, arr) => {
        const { triggerAt: nextTriggerAt } = arr[index - 1] || { triggerAt: -100000000 },
              {
                triggerAt, title, color,
              } = action,
              isBeingCommited = commitedAction === action;

        return (
          <Animated.View
            key={title}
            style={[
              styles.colorBackground,
              {
                opacity: translateX.interpolate({
                  inputRange: [-1, 0, 1],
                  outputRange: [1, 1, 0],
                }),
                transform: index === 0 ? [] : [{
                  translateX: isBeingCommited ? commitAnimationProgress.interpolate({
                    inputRange: [0, 1],
                    outputRange: [-ACTION_EDGE_WIDTH * index, 0],
                    extrapolate: 'clamp',
                  }) : translateX.interpolate({
                    inputRange: [
                      -width,
                      nextTriggerAt - HALF_FADE_DISTANCE,
                      nextTriggerAt + HALF_FADE_DISTANCE,
                    ],
                    outputRange: [
                      ((arr.length - index) * ACTION_EDGE_WIDTH) - width,
                      nextTriggerAt + ((arr.length - index) * ACTION_EDGE_WIDTH),
                      -ACTION_EDGE_WIDTH * index,
                    ],
                    extrapolate: 'clamp',
                  }),
                }],
              },
            ]}
          >
            <View style={[styles.wrapper, styles.wrapperRight, {
              backgroundColor: color,
            }]}
            >
              <Animated.View
                elevation={2}
                style={[
                  styles.wrapper,
                  styles.wrapperRight, {
                    backgroundColor: Color(color).whiten(0.3).string(),
                    opacity: commitedAction ? isBeingCommited ? 1 : 0.5 : translateX.interpolate({
                      inputRange: [
                        nextTriggerAt - HALF_FADE_DISTANCE,
                        nextTriggerAt,
                        triggerAt - HALF_FADE_DISTANCE,
                        triggerAt,
                      ],
                      outputRange: [0.5, 1, 1, 0.5],
                      extrapolate: 'clamp',
                    }),
                  },
                ]}
              >
                <AnimateIconToCenter
                  commitAnimationProgress={commitAnimationProgress}
                  isBeingCommited={isBeingCommited}
                  action={action}
                />
              </Animated.View>
            </View>

          </Animated.View>
        );
      }) }

      <Animated.View style={[styles.upperShadow, {
        transform: [{
          translateX: translateX.interpolate({
            inputRange: [-width, -0.01, 0, width],
            outputRange: [0, width - 0.01, -width, 0],
          }),
        }],
      }]}
      >
        <View
          elevation={1}
          style={styles.innerShadow}
        />
      </Animated.View>
    </>
  );
};

const SwipableRow = ({
  item, index, isSwipeDisabled,
}) => {
  const { translateX: actualTranslateX } = useContext(DraggableFlatListContext),
        { isSelected } = useContext(MultiSelectContext),
        { activeItemIndex, touchStatus, activeItem } = useContext(DragAndDropContext),
        {
          x, commitedAction, commitedItems, swipeSensitivity,
        } = useContext(ActionsContext),
        swipeSense = new Animated.Value(swipeSensitivity);

  const translateX = Animated.multiply(swipeSense, commitedAction ? x : actualTranslateX),
        isInAction = (commitedAction && commitedItems.find((id) => id === item.id)) || ([STATUS.HORIZONTAL].includes(touchStatus) && !isSwipeDisabled && ((
          (activeItemIndex === index || (isSelected(item.id) && isSelected(activeItem && activeItem.id)))
        )));

  return (
    <Animated.View style={styles.container}>
      { isInAction
        ? <ActionsPart actualTranslateX={Animated.multiply(swipeSense, actualTranslateX)} />
        : null }
      <Animated.View style={[
        styles.box,
        {
          transform: isInAction
            ? [{ translateX }]
            : [],
        },
      ]}
      >
        <ItemDetail item={item} index={index} isInAction={isInAction} />
      </Animated.View>

    </Animated.View>
  );
};


export default SwipableRow;
